import {
  addCubaCalendarDays,
  cubaWallTimeToUtc,
  getCubaTimeParts,
} from '../time/cubaTime';
import {
  addMinutesToTime,
  getActiveWindow,
  isInResultsWaitBlock,
  parseTimeToMinutes,
  resolveActiveTurnDayKey,
  resolveWindowSchedule,
} from './windowSchedule';

export type CutoffWindowState =
  | 'day_open'
  | 'awaiting_results'
  | 'night_open'
  | 'next_day'
  | 'unknown';

export interface CutoffState {
  label: string;
  hours: number;
  mins: number;
  secs: number;
  windowState: CutoffWindowState;
  /** Live countdown only while accepting plays. */
  timerLive: boolean;
  /** True when ≤5 minutes remain on a live acceptance countdown. */
  urgentClose: boolean;
  /** Total seconds remaining on a live timer (0 when frozen). */
  totalSecs: number;
}

export interface CutoffConfig {
  dayOpen?: string;
  dayClose?: string;
  nightOpen?: string;
  nightClose?: string;
  cutoffMin?: number;
}

const URGENT_CLOSE_SECS = 5 * 60;

function msUntilTarget(nowUtc: Date, targetUtc: Date) {
  const ms = Math.max(0, targetUtc.getTime() - nowUtc.getTime());
  return {
    hours: Math.floor(ms / 3_600_000),
    mins: Math.floor((ms % 3_600_000) / 60_000),
    secs: Math.floor((ms % 60_000) / 1_000),
    totalSecs: Math.floor(ms / 1_000),
  };
}

function cubaWallOnDate(
  year: number,
  month: number,
  day: number,
  hhmm: string,
): Date {
  const [h, m] = hhmm.split(':').map(Number);
  return cubaWallTimeToUtc(year, month, day, h, m, 0);
}

/** Most recent occurrence of Cuba HH:mm at or before nowUtc. */
function mostRecentCubaHHMM(nowUtc: Date, hhmm: string): Date {
  const cuba = getCubaTimeParts(nowUtc);
  let candidate = cubaWallOnDate(cuba.year, cuba.month, cuba.day, hhmm);
  if (candidate.getTime() > nowUtc.getTime()) {
    const prev = addCubaCalendarDays(cuba.year, cuba.month, cuba.day, -1);
    candidate = cubaWallOnDate(prev.year, prev.month, prev.day, hhmm);
  }
  return candidate;
}

function frozenState(windowState: CutoffWindowState, label: string): CutoffState {
  return {
    label,
    hours: 0,
    mins: 0,
    secs: 0,
    windowState,
    timerLive: false,
    urgentClose: false,
    totalSecs: 0,
  };
}

/**
 * Countdown toward acceptance close while a turn is open.
 * Frozen (no live tick) during results-wait blocks and between cycles.
 */
export function computeCutoffState(nowUtc: Date, config: CutoffConfig | null | undefined): CutoffState {
  if (!config?.dayClose) {
    return frozenState('unknown', 'Loading…');
  }

  const schedule = resolveWindowSchedule(config);
  const active = getActiveWindow(config, nowUtc);
  const waiting = isInResultsWaitBlock(config, nowUtc);

  if (active === 'day' || active === 'night') {
    const closeHHMM = active === 'day' ? schedule.dayClose : schedule.nightClose;
    const acceptCloseHHMM = addMinutesToTime(closeHHMM, -schedule.cutoffMin);
    let targetUtc = mostRecentCubaHHMM(nowUtc, acceptCloseHHMM);
    // Accept-close is in the future within this open turn.
    if (targetUtc.getTime() <= nowUtc.getTime()) {
      const cuba = getCubaTimeParts(nowUtc);
      targetUtc = cubaWallOnDate(cuba.year, cuba.month, cuba.day, acceptCloseHHMM);
      if (targetUtc.getTime() <= nowUtc.getTime()) {
        const next = addCubaCalendarDays(cuba.year, cuba.month, cuba.day, 1);
        targetUtc = cubaWallOnDate(next.year, next.month, next.day, acceptCloseHHMM);
      }
    }
    // Overnight day: accept close may be earlier clock time tomorrow — handled above.
    // Prefer the soonest future accept-close.
    const cuba = getCubaTimeParts(nowUtc);
    const todayTarget = cubaWallOnDate(cuba.year, cuba.month, cuba.day, acceptCloseHHMM);
    if (todayTarget.getTime() > nowUtc.getTime()) {
      targetUtc = todayTarget;
    } else {
      const next = addCubaCalendarDays(cuba.year, cuba.month, cuba.day, 1);
      targetUtc = cubaWallOnDate(next.year, next.month, next.day, acceptCloseHHMM);
    }

    const { hours, mins, secs, totalSecs } = msUntilTarget(nowUtc, targetUtc);
    return {
      label: active === 'day' ? 'day_open' : 'night_open',
      hours,
      mins,
      secs,
      windowState: active === 'day' ? 'day_open' : 'night_open',
      timerLive: true,
      urgentClose: totalSecs > 0 && totalSecs <= URGENT_CLOSE_SECS,
      totalSecs,
    };
  }

  if (waiting) {
    return frozenState('awaiting_results', 'awaiting_results');
  }

  return frozenState('next_day', 'next_day');
}

/**
 * Turn whose results should already exist when accepting the current turn
 * (or while inside that turn's wait block). Used for delayed-results UI.
 */
export function resolvePrecedingTurn(
  config: CutoffConfig | null | undefined,
  nowUtc: Date = new Date(),
): { dayKey: string; window: 'day' | 'night' } | null {
  if (!config?.dayClose) return null;
  const schedule = resolveWindowSchedule(config);
  const active = getActiveWindow(config, nowUtc);
  const waiting = isInResultsWaitBlock(config, nowUtc);

  if (waiting === 'day' || active === 'night') {
    const closeAt = mostRecentCubaHHMM(nowUtc, schedule.dayClose);
    const beforeClose = new Date(closeAt.getTime() - 60_000);
    return {
      window: 'day',
      dayKey: resolveActiveTurnDayKey(config, 'day', beforeClose),
    };
  }

  if (waiting === 'night' || active === 'day') {
    const closeAt = mostRecentCubaHHMM(nowUtc, schedule.nightClose);
    const beforeClose = new Date(closeAt.getTime() - 60_000);
    return {
      window: 'night',
      dayKey: resolveActiveTurnDayKey(config, 'night', beforeClose),
    };
  }

  return null;
}

/** Session id helper for drawSessions maps / lists. */
export function precedingTurnSessionKey(
  config: CutoffConfig | null | undefined,
  nowUtc: Date = new Date(),
): string | null {
  const turn = resolvePrecedingTurn(config, nowUtc);
  if (!turn) return null;
  return `${turn.dayKey}_${turn.window}`;
}

// Re-export for tests that poke minutes math
export { parseTimeToMinutes };
